import { createSlice, createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import * as R from "ramda";
import { ActionType } from "../actionTypes";
import {
  Notebook,
  NotebookPage,
  PageBlocksMap,
  PagePublishedMap,
} from "../types/note";
import { NoteBlock } from "../types/notebook/note/block";
import { NoteEdits, newHtmlBlock } from "../types/notebook/note/block/html";
import { PageSubPagesMap } from "../types/sideNav";
import { isTableBlock } from "../types/notebook/note/block/table";
import type { RootState } from "../reducer";

export const notebookPagesAdapter = createEntityAdapter<NotebookPage, string>({
  selectId: (page) => page["page-id"],
});

export interface PagesState {
  notebookPages: EntityState<NotebookPage, string>;
  pageBlocks: PageBlocksMap;
  selectedNotebookPageId?: string;
  notebook?: Notebook;
  publishedPagesMap: PagePublishedMap;
  pageSubPagesMap: PageSubPagesMap;
  noteEdits: NoteEdits;
  deleteTables?: string[];
  blockToFocus?: number;
  rootFolderId?: string;
}

const initialState: PagesState = {
  notebookPages: notebookPagesAdapter.getInitialState(),
  pageBlocks: {},
  publishedPagesMap: {},
  pageSubPagesMap: {},
  noteEdits: {},
};

// ---------- helpers that work on PagesState ----------

const flushNoteEdits = (state: PagesState): PagesState => {
  const { noteEdits, selectedNotebookPageId, pageBlocks } = state;
  if (!selectedNotebookPageId) return { ...state, noteEdits: {} };
  const blocks = pageBlocks[selectedNotebookPageId] ?? [];
  const newBlocks = blocks.map((block, i) => {
    const edit = noteEdits[i];
    return edit ? { ...block, html: edit } : block;
  });
  const newPageBlocks: PageBlocksMap = {
    ...pageBlocks,
    [selectedNotebookPageId]: newBlocks,
  };
  return { ...state, pageBlocks: newPageBlocks, noteEdits: {} };
};

const selectedPageBlocks = (state: PagesState): NoteBlock[] => {
  const { selectedNotebookPageId, pageBlocks } = state;
  return selectedNotebookPageId
    ? (pageBlocks[selectedNotebookPageId] ?? [])
    : [];
};

const setSelectedPageBlocks = (
  state: PagesState,
  newBlocks: NoteBlock[],
): PagesState => {
  const { selectedNotebookPageId, pageBlocks } = state;
  if (!selectedNotebookPageId) return state;
  const newPageBlocks: PageBlocksMap = {
    ...pageBlocks,
    [selectedNotebookPageId]: newBlocks,
  };
  return { ...state, pageBlocks: newPageBlocks };
};

// ---------- slice ----------

export const pagesSlice = createSlice({
  name: "pages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(ActionType.RESET_NOTEBOOK_WORKSPACE, () => initialState);

    // SET_PAGE_BLOCKS
    builder.addCase(ActionType.SET_PAGE_BLOCKS, (state, action: any) => {
      const { pageId, blocks } = action;
      return {
        ...state,
        pageBlocks: { ...state.pageBlocks, [pageId]: blocks },
      };
    });

    // SET_MULTIPLE_PAGE_BLOCKS
    builder.addCase(ActionType.SET_MULTIPLE_PAGE_BLOCKS, (state, action: any) => {
      return { ...state, pageBlocks: { ...state.pageBlocks, ...action.pageBlocksMap } };
    });

    // ADD_NOTEBOOK_PAGES
    builder.addCase(ActionType.ADD_NOTEBOOK_PAGES, (state, action: any) => {
      const notebookPages: NotebookPage[] = action.notebookPages;
      const pageSubPagesMap = R.groupBy(
        (page: NotebookPage) => page["folder-id"],
        notebookPages,
      ) as PageSubPagesMap;
      notebookPagesAdapter.upsertMany(state.notebookPages, action.notebookPages);
      const subPagesFromState = (pageId: string) =>
        state.pageSubPagesMap[pageId] ?? [];
      const newSubPageEntries = Object.fromEntries(
        Object.keys(pageSubPagesMap).map((pageId) => [
          pageId,
          R.sortWith(
            [
              R.ascend((page: NotebookPage) =>
                page["page-data"].name.toLowerCase(),
              ),
              R.ascend((page: NotebookPage) => page["page-id"]),
            ],
            R.uniqBy(
              (page: NotebookPage) => page["page-id"],
              [
                ...(pageSubPagesMap[pageId] ?? []),
                ...subPagesFromState(pageId),
              ],
            ),
          ),
        ]),
      );
      state.pageSubPagesMap = { ...state.pageSubPagesMap, ...newSubPageEntries };
    });

    // REMOVE_NOTEBOOK_PAGES
    builder.addCase(
      ActionType.REMOVE_NOTEBOOK_PAGES,
      (state, action: any) => {
        const notebookPages: NotebookPage[] = action.notebookPages;
        const pageIds = notebookPages.map((p) => p["page-id"]);
        const pageIdSet = new Set(pageIds);
        notebookPagesAdapter.removeMany(
          state.notebookPages,
          action.notebookPages.map((p: NotebookPage) => p["page-id"]),
        );
        const oldNotebook = state.notebook;
        const newNotebookPages = oldNotebook?.pages.filter(
          (p) => !pageIdSet.has(p["page-id"]),
        );
        const newSelectedNotebookPageId =
          state.selectedNotebookPageId &&
          !pageIdSet.has(state.selectedNotebookPageId)
            ? state.selectedNotebookPageId
            : undefined;
        const newNotebook: Notebook | undefined = oldNotebook
          ? { ...oldNotebook, pages: newNotebookPages as NotebookPage[] }
          : undefined;
        state.notebook = newNotebook;
        state.selectedNotebookPageId = newSelectedNotebookPageId;
      },
    );

    // SET_SELECTED_NOTEBOOK_PAGE_ID
    builder.addCase(
      ActionType.SET_SELECTED_NOTEBOOK_PAGE_ID,
      (state, action: any) => {
        const { pageId } = action;
        return {
          ...state,
          selectedNotebookPageId: pageId,
          noteEdits: {},
        };
      },
    );

    // SET_NOTEBOOK
    builder.addCase(ActionType.SET_NOTEBOOK, (state, action: any) => {
      const notebook = action.notebook;
      const rootFolderId = notebook?.pages[0]?.["folder-id"];
      return { ...state, notebook: action.notebook, rootFolderId };
    });

    // SET_NOTE_EDIT (single index)
    builder.addCase(ActionType.SET_NOTE_EDIT, (state, action: any) => {
      const { index, edit } = action;
      const newEdits = R.assoc(index, edit, state.noteEdits);
      return { ...state, noteEdits: newEdits };
    });

    // SET_NOTE_EDITS (full replacement)
    builder.addCase(ActionType.SET_NOTE_EDITS, (state, action: any) => {
      return { ...state, noteEdits: action.noteEdits };
    });

    // FLUSH_NOTE_EDITS
    builder.addCase(ActionType.FLUSH_NOTE_EDITS, (state) => {
      return flushNoteEdits(state);
    });

    // MOVE_NOTE_BLOCK
    builder.addCase(ActionType.MOVE_NOTE_BLOCK, (state, action: any) => {
      const { firstIndex, secondIndex } = action;
      const flushed = flushNoteEdits(state);
      const { selectedNotebookPageId, pageBlocks } = flushed;
      if (!selectedNotebookPageId) return flushed;
      const blocks = pageBlocks[selectedNotebookPageId] ?? [];
      const block = blocks[firstIndex];
      const moveUp = secondIndex < firstIndex;
      const insertIndex = moveUp ? secondIndex : secondIndex + 1;
      const removeIndex = moveUp ? firstIndex + 1 : firstIndex;
      const moveIndex = R.pipe(R.insert(insertIndex, block), R.remove(removeIndex, 1));
      const newBlocks = moveIndex(blocks);
      const newBlocksMap: PageBlocksMap = {
        ...pageBlocks,
        [selectedNotebookPageId]: newBlocks,
      };
      return { ...flushed, pageBlocks: newBlocksMap };
    });

    // SET_PAGE_NAME
    builder.addCase(ActionType.SET_PAGE_NAME, (state, action: any) => {
      const { pageId, name } = action;
      const entity = state.notebookPages.entities[pageId];
      if (entity) {
        entity["page-data"] = { ...entity["page-data"], name };
      }
    });

    // SET_PAGE_PUBLISHED
    builder.addCase(ActionType.SET_PAGE_PUBLISHED, (state, action: any) => {
      const { pageId, published } = action;
      return {
        ...state,
        publishedPagesMap: { ...state.publishedPagesMap, [pageId]: published },
      };
    });

    // SET_BLOCK_TO_FOCUS
    builder.addCase(ActionType.SET_BLOCK_TO_FOCUS, (state, action: any) => {
      return { ...state, blockToFocus: action.blockToFocus };
    });

    // FOCUS_NEXT_BLOCK
    builder.addCase(ActionType.FOCUS_NEXT_BLOCK, (state, action: any) => {
      const { focusedIndex } = action;
      const blocks = selectedPageBlocks(state);
      const isLastIndex = blocks.length - 1 === focusedIndex;
      return isLastIndex ? state : { ...state, blockToFocus: focusedIndex + 1 };
    });

    // FOCUS_PREVIOUS_BLOCK
    builder.addCase(ActionType.FOCUS_PREVIOUS_BLOCK, (state, action: any) => {
      const { focusedIndex } = action;
      const isFirstIndex = 0 === focusedIndex;
      return isFirstIndex ? state : { ...state, blockToFocus: focusedIndex - 1 };
    });

    // INSERT_BLOCK
    builder.addCase(ActionType.INSERT_BLOCK, (state, action: any) => {
      const { index, appId, pageId, block } = action;
      const flushed = flushNoteEdits(state);
      const blocks = selectedPageBlocks(flushed);
      const addedBlock = block ?? newHtmlBlock({ appId, pageId, html: "" });
      const newBlocks = R.insert(index + 1, addedBlock, blocks);
      const withBlockEdits = setSelectedPageBlocks(flushed, newBlocks);
      return { ...withBlockEdits, blockToFocus: index + 1 };
    });

    // DELETE_BLOCK
    builder.addCase(ActionType.DELETE_BLOCK, (state, action: any) => {
      const { focusedIndex } = action;
      const flushed = flushNoteEdits(state);
      const { deleteTables } = flushed;
      const blocks = selectedPageBlocks(flushed);
      const block = blocks[focusedIndex];
      const additionalDeleteTables = isTableBlock(block)
        ? [block["table-id"]]
        : [];
      const newDeleteTables = isTableBlock(block)
        ? R.uniq([...(deleteTables ?? []), ...additionalDeleteTables])
        : deleteTables;
      const newBlocks = R.remove(focusedIndex, 1, blocks);
      const newState = setSelectedPageBlocks(flushed, newBlocks);
      const newIndex = Math.max(focusedIndex - 1, 0);
      return {
        ...newState,
        blockToFocus: newIndex,
        deleteTables: newDeleteTables,
      };
    });

    // SET_DELETE_TABLES
    builder.addCase(ActionType.SET_DELETE_TABLES, (state, action: any) => {
      return { ...state, deleteTables: action.deleteTables };
    });

    // ADD_SUB_PAGES (from sideNav reducer)
    builder.addCase(ActionType.ADD_SUB_PAGES, (state, action: any) => {
      const mapFromAction: PageSubPagesMap = action.pageSubPagesMap;
      const mapFromState = state.pageSubPagesMap;
      const subPagesFromState = (pageId: string) => mapFromState[pageId] ?? [];
      const subPagesFromAction = (pageId: string) =>
        mapFromAction[pageId] ?? [];
      const newEntriesForPage = (pageId: string): NotebookPage[] =>
        R.sortWith(
          [
            R.ascend((page: NotebookPage) =>
              page["page-data"].name.toLowerCase(),
            ),
            R.ascend((page: NotebookPage) => page["page-id"]),
          ],
          R.uniqBy(
            (page: NotebookPage) => page["page-id"],
            [...subPagesFromAction(pageId), ...subPagesFromState(pageId)],
          ),
        );
      const newEntries = Object.fromEntries(
        Object.keys(mapFromAction).map((pageId) => [
          pageId,
          newEntriesForPage(pageId),
        ]),
      );
      const newMap = { ...mapFromState, ...newEntries };
      return { ...state, pageSubPagesMap: newMap };
    });
  },
});

export default pagesSlice.reducer;

export const notebookPagesSelectors = notebookPagesAdapter.getSelectors(
  (state: RootState) => state.pages.notebookPages,
);
